// MARK://dhk8/pos/lib/bridge/relay-job-tracker
// Seguimiento de UN PrintJob del Print Relay desde que se encola hasta que sale
// el papel (o falla). Existe porque en mode 'relay' `printReceipt`/`kickDrawer`
// resuelven APENAS el job entra a la cola (status 'pending') — NO cuando imprime.
// El papel sale después, cuando el Bridge HOST jala el job y hace ack. Sin este
// tracker el usuario no se entera del resultado real (solo "encolado").
//
// Qué hace: por cada jobId que registras (`trackRelayJob`), poll-ea
// `relay-client.getJob` hasta un estado terminal, exponiendo las transiciones
// para la UI:
//   enviado → imprimiendo → ✅ impreso | ⚠️ reintentando (x/3) | ❌ falló (motivo)
//
// Mapeo de estados (contrato docs/print-relay-design.md):
//   pending  + attempts=0           → 'enviado'      (en cola, aún no reclamado)
//   pending  + attempts>0           → 'reintentando' (falló e irá de nuevo; muestra x/max)
//   claimed                         → 'imprimiendo'  (el Bridge lo reclamó)
//   done                            → 'impreso'      (terminal ✅)
//   failed                          → 'fallido'      (terminal ❌ + motivo)
//
// Diseño: store Zustand global + `<RelayJobToaster/>` (montado una vez en el
// Shell) que pinta los jobs activos. Los callsites de impresión son
// fire-and-forget desde muchos lugares (PaymentDialog, FastBar, order-store,
// CashoutPage), así que un surface global es lo idiomático — el caller solo
// llama `trackRelayJob(jobId, label)` y se olvida.
//
// El tracker es además dueño del history FINAL (printing-config-store): escribe
// 'ok'/'error' cuando el job alcanza un estado terminal, con el resultado REAL.
// (Los callsites en relay YA NO escriben 'ok' al encolar — sería mentira.)
//
// HOST (loopback) nunca usa esto: ahí printReceipt resuelve cuando el papel
// salió y no hay jobId → no se trackea nada, history se escribe como siempre.
//
// Tolerante: errores transitorios de getJob (red) se reintentan; si el job no
// resuelve en MAX_POLLS se marca 'fallido' con timeout para no dejar un toast
// colgado para siempre.

import { create } from 'zustand'
import { getJob, type RelayJob, type RelayJobKind } from './relay-client.js'
import { pushPrintHistory } from './printing-config-store.js'

/** Estado legible (UI) derivado del RelayJob del backend. */
export type TrackedPhase =
  | 'enviado'       // pending, attempts=0 — en cola
  | 'imprimiendo'   // claimed — el Bridge lo reclamó y está imprimiendo
  | 'reintentando'  // pending, attempts>0 — falló un intento, reintentará
  | 'impreso'       // done — terminal ✅
  | 'fallido'       // failed | timeout — terminal ❌

export interface TrackedJob {
  /** jobId del relay (PrintJob.id). */
  id: string
  /** Etiqueta humana del trabajo ("Ticket", "Comanda", "Cajón", …) para el toast. */
  label: string
  /** Clase del job (receipt/kitchen/raw/drawer) — informativo. */
  kind?: RelayJobKind | string
  phase: TrackedPhase
  attempts: number
  maxAttempts: number
  /** Motivo del último fallo (solo phase 'reintentando' | 'fallido'). */
  lastError?: string | null
  /** ¿Estado terminal? (impreso | fallido) — el toaster lo auto-descarta. */
  done: boolean
  /** ISO en que se empezó a seguir — para ordenar/auto-dismiss. */
  startedAt: string
}

interface RelayJobTrackerState {
  jobs: Record<string, TrackedJob>
  /** Empieza (o reanuda) el seguimiento de un job. Idempotente por id. */
  track: (jobId: string, label: string, kind?: RelayJobKind | string) => void
  /** Quita un job del surface (lo llama el toaster al auto-dismiss o el usuario). */
  dismiss: (jobId: string) => void
  /** Reemplaza el estado de un job tras un poll. */
  _set: (jobId: string, patch: Partial<TrackedJob>) => void
}

// Poll: arranca rápido (el papel suele salir en <1s si la estación está online)
// y se relaja. Hasta ~60s en total antes de declarar timeout — un Bridge offline
// puede tardar en volver, pero el toast no debe vivir para siempre; el panel de
// Ajustes (listJobs) sigue mostrando el job cuando finalmente imprima.
const POLL_INTERVAL_MS = 1_200
const MAX_POLLS = 50 // 50 × 1.2s = 60s
// Tras alcanzar un estado terminal, el toast se queda visible este tiempo y se va.
const TERMINAL_LINGER_MS = 6_000

/** Deriva la fase legible + flag terminal de un RelayJob crudo. */
function phaseOf(job: RelayJob): { phase: TrackedPhase; done: boolean } {
  switch (job.status) {
    case 'claimed':
      return { phase: 'imprimiendo', done: false }
    case 'done':
      return { phase: 'impreso', done: true }
    case 'failed':
      return { phase: 'fallido', done: true }
    case 'pending':
    default:
      // pending con intentos previos = ya falló al menos una vez y reintentará.
      return { phase: (job.attempts ?? 0) > 0 ? 'reintentando' : 'enviado', done: false }
  }
}

// Pollers vivos por jobId (para no duplicar timers si se trackea dos veces).
const pollers = new Map<string, ReturnType<typeof setTimeout>>()

function clearPoller(jobId: string): void {
  const t = pollers.get(jobId)
  if (t) { clearTimeout(t); pollers.delete(jobId) }
}

export const useRelayJobTracker = create<RelayJobTrackerState>((set, get) => ({
  jobs: {},

  track: (jobId, label, kind) => {
    if (!jobId) return
    // Si ya lo estamos siguiendo, no reinicies el poller (idempotente).
    if (get().jobs[jobId] && pollers.has(jobId)) return
    const seed: TrackedJob = {
      id: jobId,
      label,
      ...(kind ? { kind } : {}),
      phase: 'enviado',
      attempts: 0,
      maxAttempts: 3,
      done: false,
      startedAt: new Date().toISOString(),
    }
    set((s) => ({ jobs: { ...s.jobs, [jobId]: seed } }))
    startPolling(jobId, label, set, get)
  },

  dismiss: (jobId) => {
    clearPoller(jobId)
    set((s) => {
      if (!s.jobs[jobId]) return s
      const next = { ...s.jobs }
      delete next[jobId]
      return { jobs: next }
    })
  },

  _set: (jobId, patch) => {
    set((s) => {
      const cur = s.jobs[jobId]
      if (!cur) return s
      return { jobs: { ...s.jobs, [jobId]: { ...cur, ...patch } } }
    })
  },
}))

/** Arranca el loop de poll para un job. Cada tick consulta getJob, actualiza la
 *  fase y, al terminal, escribe el history real y agenda el auto-dismiss. */
function startPolling(
  jobId: string,
  label: string,
  set: (fn: (s: RelayJobTrackerState) => Partial<RelayJobTrackerState>) => void,
  get: () => RelayJobTrackerState,
): void {
  let polls = 0
  let transientErrors = 0

  const finalize = (phase: 'impreso' | 'fallido', error?: string | null, attempts?: number, maxAttempts?: number): void => {
    clearPoller(jobId)
    get()._set(jobId, {
      phase,
      done: true,
      ...(error !== undefined ? { lastError: error } : {}),
      ...(attempts !== undefined ? { attempts } : {}),
      ...(maxAttempts !== undefined ? { maxAttempts } : {}),
    })
    // History REAL (no el del momento del enqueue): el panel de Ajustes › Últimas
    // impresiones refleja el desenlace verdadero.
    pushPrintHistory(label, phase === 'impreso' ? 'ok' : 'error', error ?? undefined)
    // Deja el toast un rato y luego límpialo si el usuario no lo cerró antes.
    setTimeout(() => {
      const cur = get().jobs[jobId]
      if (cur && cur.done) get().dismiss(jobId)
    }, TERMINAL_LINGER_MS)
  }

  const tick = async (): Promise<void> => {
    // Si el job ya fue descartado (usuario cerró el toast), corta el loop.
    if (!get().jobs[jobId]) { clearPoller(jobId); return }
    polls += 1
    try {
      const job = await getJob(jobId)
      transientErrors = 0
      const { phase, done } = phaseOf(job)
      const attempts = job.attempts ?? 0
      const maxAttempts = job.maxAttempts ?? 3
      if (done) {
        finalize(phase === 'impreso' ? 'impreso' : 'fallido', job.lastError ?? null, attempts, maxAttempts)
        return
      }
      get()._set(jobId, { phase, attempts, maxAttempts, lastError: job.lastError ?? null, ...(job.kind ? { kind: job.kind } : {}) })
    } catch {
      // Error transitorio (red/5xx): no tumbes el seguimiento; reintenta unas
      // veces antes de rendirte. No cuenta como intento de impresión.
      transientErrors += 1
      if (transientErrors >= 5) {
        finalize('fallido', 'No se pudo consultar el estado del trabajo')
        return
      }
    }
    if (polls >= MAX_POLLS) {
      finalize('fallido', 'El trabajo sigue pendiente (la estación no respondió a tiempo)')
      return
    }
    pollers.set(jobId, setTimeout(() => { void tick() }, POLL_INTERVAL_MS))
  }

  // Primer poll inmediato (no esperar el intervalo) — el papel puede salir ya.
  pollers.set(jobId, setTimeout(() => { void tick() }, 0))
}

/** API imperativa para los callsites de impresión (no-React). Empieza a seguir
 *  un job recién encolado. Llamar SOLO en mode 'relay' (cuando printReceipt/
 *  kickDrawer devolvieron un jobId). No-op si jobId vacío. */
export function trackRelayJob(jobId: string | undefined | null, label: string, kind?: RelayJobKind | string): void {
  if (!jobId) return
  useRelayJobTracker.getState().track(jobId, label, kind)
}

/** Etiqueta + tono visual por fase, para el toaster. */
export function phaseView(phase: TrackedPhase, attempts: number, maxAttempts: number): {
  label: string
  tone: 'pending' | 'progress' | 'retry' | 'ok' | 'fail'
} {
  switch (phase) {
    case 'enviado':      return { label: 'Enviado', tone: 'pending' }
    case 'imprimiendo':  return { label: 'Imprimiendo…', tone: 'progress' }
    case 'reintentando': return { label: `Reintentando (${attempts}/${maxAttempts})`, tone: 'retry' }
    case 'impreso':      return { label: 'Impreso', tone: 'ok' }
    case 'fallido':      return { label: 'Falló', tone: 'fail' }
    default:             return { label: String(phase), tone: 'pending' }
  }
}
